"use client";

import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { reintentSession, type ReintentResult } from "@/lib/api";
import { parseIntentDirections, scoreBgColor, scoreTextColor } from "@/lib/utils";

interface Props {
  sessionId: string;
  intentScore: number | null | undefined;
  intentDirectionsJson: string | null | undefined;
  intentAt: number | null | undefined;
  onUpdate: (r: ReintentResult) => void;
}

export function IntentPanel({ sessionId, intentScore, intentDirectionsJson, intentAt, onUpdate }: Props) {
  const [loading, setLoading] = useState(false);

  const handleRerun = () => {
    setLoading(true);
    reintentSession(sessionId)
      .then(onUpdate)
      .finally(() => setLoading(false));
  };

  const directions = parseIntentDirections(intentDirectionsJson);
  const hasScore = intentScore != null;
  const pct = hasScore ? Math.round(intentScore! * 100) : 0;

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-medium">意向</h3>
        <Button variant="outline" size="sm" onClick={handleRerun} disabled={loading}>
          {loading ? "评估中..." : intentAt ? "重新评估" : "评估"}
        </Button>
      </div>

      {!hasScore && !intentAt ? (
        <p className="text-sm text-muted-foreground">未执行</p>
      ) : !hasScore ? (
        <p className="text-sm text-muted-foreground">无有效对话（轮数小于 1）</p>
      ) : (
        <div className="space-y-2">
          <div className="flex items-baseline gap-2">
            <span className={`text-2xl font-semibold tabular-nums ${scoreTextColor(intentScore)}`}>
              {pct}%
            </span>
          </div>
          <div className="h-2 overflow-hidden rounded bg-muted">
            <div
              className={`h-full transition-[width] duration-150 ${scoreBgColor(intentScore)}`}
              style={{ width: `${pct}%` }}
            />
          </div>
          {directions.length > 0 ? (
            <div className="flex flex-wrap gap-1 pt-1">
              {directions.map((d) => (
                <Badge key={d} variant="outline" className="text-xs">{d}</Badge>
              ))}
            </div>
          ) : (
            <p className="text-xs text-muted-foreground pt-1">无明确意向方向</p>
          )}
        </div>
      )}
    </div>
  );
}
